import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { Modal } from 'ui/base/modal';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { Weekday, getMicroWeekday } from 'assets/enums/weekdays/weekdays';
import baseStyles from './calendar_input.module.css';
import CalendarInputMessages from './calendar_input_messages';
import { ButtonClickEvent } from 'ui/base/types';

import {
  CalendarInputType,
  CalendarInputStore,
  CalendarFirstInputStore,
  CalendarFinalInputStore,
} from './calendar_input_store';


type CalendarInputStyles = {
  box: string;
  placeholder: string;
  modalContainer: string;
  header: string;
  commit: string;
  calendar: string;
  calendarHeader: string;
  calendarHeaderText: string;
  calendarHeaderNavNext: string;
  calendarHeaderNavPrev: string;
  calendarHeaderNavNextDisabled: string;
  calendarHeaderNavPrevDisabled: string;
  calendarBody: string;
  calendarBodyRow: string;
  calendarBodyCellType: string;
  calendarBodyCellDefault: string;
  calendarBodyCellDisabled: string;
  calendarBodyCellHighlight: string;
  calendarBodyCellSelected: string;
  calendarBodyBorderFirst: string;
  calendarBodyBorderFinal: string;
  calendarBodyContentDefault: string;
  calendarBodyContentSelected: string;
};

const themedStyles: ThemedStyles<CalendarInputStyles> = {
  [Theme.DAYLIGHT]: {
    box: baseStyles.daylightBox,
    placeholder: baseStyles.daylightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.daylightCommit,
    calendar: baseStyles.baseCalendar,
    calendarHeader: baseStyles.baseCalendarHeader,
    calendarHeaderText: baseStyles.baseCalendarHeaderText,
    calendarHeaderNavNext: baseStyles.daylightCalendarHeaderNavNext,
    calendarHeaderNavPrev: baseStyles.daylightCalendarHeaderNavPrev,
    calendarHeaderNavNextDisabled: baseStyles.daylightCalendarHeaderNavNextDisabled,
    calendarHeaderNavPrevDisabled: baseStyles.daylightCalendarHeaderNavPrevDisabled,
    calendarBody: baseStyles.baseCalendarBody,
    calendarBodyRow: baseStyles.baseCalendarBodyRow,
    calendarBodyCellType: baseStyles.baseCalendarBodyCellHeader,
    calendarBodyCellDefault: baseStyles.baseCalendarBodyCellDefault,
    calendarBodyCellDisabled: baseStyles.baseCalendarBodyCellDisabled,
    calendarBodyCellHighlight: baseStyles.daylightCalendarBodyCellHighlight,
    calendarBodyCellSelected: baseStyles.daylightCalendarBodyCellSelected,
    calendarBodyBorderFirst: baseStyles.baseCalendarBodyBorderFirst,
    calendarBodyBorderFinal: baseStyles.baseCalendarBodyBorderFinal,
    calendarBodyContentDefault: baseStyles.baseCalendarBodyContentDefault,
    calendarBodyContentSelected: baseStyles.daylightCalendarBodyContentSelected,
  },
  [Theme.MIDNIGHT]: {
    box: baseStyles.midnightBox,
    placeholder: baseStyles.midnightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.midnightCommit,
    calendar: baseStyles.baseCalendar,
    calendarHeader: baseStyles.baseCalendarHeader,
    calendarHeaderText: baseStyles.baseCalendarHeaderText,
    calendarHeaderNavNext: baseStyles.midnightCalendarHeaderNavNext,
    calendarHeaderNavPrev: baseStyles.midnightCalendarHeaderNavPrev,
    calendarHeaderNavNextDisabled: baseStyles.midnightCalendarHeaderNavNextDisabled,
    calendarHeaderNavPrevDisabled: baseStyles.midnightCalendarHeaderNavPrevDisabled,
    calendarBody: baseStyles.baseCalendarBody,
    calendarBodyRow: baseStyles.baseCalendarBodyRow,
    calendarBodyCellType: baseStyles.baseCalendarBodyCellHeader,
    calendarBodyCellDefault: baseStyles.baseCalendarBodyCellDefault,
    calendarBodyCellDisabled: baseStyles.baseCalendarBodyCellDisabled,
    calendarBodyCellHighlight: baseStyles.midnightCalendarBodyCellHighlight,
    calendarBodyCellSelected: baseStyles.midnightCalendarBodyCellSelected,
    calendarBodyBorderFirst: baseStyles.baseCalendarBodyBorderFirst,
    calendarBodyBorderFinal: baseStyles.baseCalendarBodyBorderFinal,
    calendarBodyContentDefault: baseStyles.baseCalendarBodyContentDefault,
    calendarBodyContentSelected: baseStyles.midnightCalendarBodyContentSelected,
  },
};

// Overriding default ORDERED_WEEKDAYS because zero is considered Sunday when
// using the Javascript Date object
export const ORDERED_WEEKDAYS: readonly Weekday[] = [
  Weekday.SUNDAY,
  Weekday.MONDAY,
  Weekday.TUESDAY,
  Weekday.WEDNESDAY,
  Weekday.THURSDAY,
  Weekday.FRIDAY,
  Weekday.SATURDAY,
];

type CalendarInputYearPickerProps = {
  store: CalendarInputStore;
};
const CalendarInputYearPicker = observer(({ store }: CalendarInputYearPickerProps) => {
  const styles = useStyles(themedStyles);

  const dateLocale = localeToLanguageCode(GlobalLocaleStore.get());

  const cellDateFormat: Intl.DateTimeFormatOptions = {
    year: 'numeric',
  };

  const viewDecade = store.viewDecade.value;

  let tempDateFirst, tempDateFinal;
  const tempDate = new Date(store.tempDateObject);
  tempDate.setMonth(0);
  tempDate.setDate(1);
  switch (store.type) {
    case CalendarInputType.FIRST:
      tempDateFirst = new Date(store.tempDateObject);
      tempDateFinal = new Date((store as CalendarFirstInputStore).final.committedDateObject);
      break;
    case CalendarInputType.FINAL:
      tempDateFirst = new Date((store as CalendarFinalInputStore).first.committedDateObject);
      tempDateFinal = new Date(store.tempDateObject);
      break;
    default:
      tempDateFirst = tempDateFinal = new Date(store.tempDateObject);
      break;
  }
  tempDateFirst.setMonth(0);
  tempDateFirst.setDate(1);
  tempDateFinal.setMonth(0);
  tempDateFinal.setDate(1);

  const viewDateFirst = new Date(viewDecade + 0, 0, 1);
  const viewDateFinal = new Date(viewDecade + 10, 0, 0);

  const viewDateFirstYear = viewDateFirst.getFullYear();
  const viewDateFinalYear = viewDateFinal.getFullYear();

  const calendarHasPrev = (store.type === CalendarInputType.SINGLE ||
                           store.type === CalendarInputType.FIRST ||
                           (store.type === CalendarInputType.FINAL &&
                            tempDateFirst <= new Date(viewDecade + 0, 0, 0)));
  const calendarHasNext = (store.type === CalendarInputType.SINGLE ||
                           (store.type === CalendarInputType.FIRST &&
                            new Date(viewDecade + 10, 0, 1) <= tempDateFinal) ||
                           store.type === CalendarInputType.FINAL);

  const calendarArray = Array(5).fill(null).map(() => Array(2).fill(null));
  for (let viewYear = viewDateFirstYear; viewYear <= viewDateFinalYear; viewYear++) {
    const calendarDate = new Date(viewYear, 0);
    const index = viewYear - viewDecade;
    const row = Math.floor(index / 2);
    const col = index % 2;
    calendarArray[row][col] = {
      decade: viewDecade, year: viewYear,
      isFirst: calendarDate.getTime() === tempDateFirst.getTime(),
      isFinal: calendarDate.getTime() === tempDateFinal.getTime(),
      isSelected: calendarDate.getTime() === tempDate.getTime(),
      isHighlight: (tempDateFirst <= calendarDate &&
                    calendarDate <= tempDateFinal),
      isSelectable: (store.type === CalendarInputType.SINGLE ||
                     (store.type === CalendarInputType.FIRST &&
                      calendarDate <= tempDateFinal) ||
                     (store.type === CalendarInputType.FINAL &&
                      tempDateFirst <= calendarDate)),
    };
  }

  return (
    <div className={styles.calendar}>
      <div className={styles.calendarHeader}>
        <div
          className={
            calendarHasPrev ?
            styles.calendarHeaderNavPrev :
            styles.calendarHeaderNavPrevDisabled
          }
          onClick={
            calendarHasPrev ? () => {
              const newViewDate = new Date(viewDecade - 10, 0);
              store.viewDecade.setValue(newViewDate.getFullYear());
            } : undefined
          }/>
        <div className={styles.calendarHeaderText}>
        </div>
        <div
          className={
            calendarHasNext ?
            styles.calendarHeaderNavNext :
            styles.calendarHeaderNavNextDisabled
          }
          onClick={
            calendarHasNext ? () => {
              const newViewDate = new Date(viewDecade + 10, 0);
              store.viewDecade.setValue(newViewDate.getFullYear());
            } : undefined
          }/>
      </div>
      <div className={styles.calendarBody}>
        {calendarArray.map((yearArray, yearArrayIndex) => {
          return (
            <div
              key={`year-row-${yearArrayIndex}`}
              className={styles.calendarBodyRow}>
              {yearArray.map((yearObject, yearObjectIndex) => {
                return (
                  <div
                    key={`year-cell-${yearArrayIndex}-${yearObjectIndex}`}
                    className={[
                      yearObject?.isHighlight ?
                      yearObject.isFirst && yearObject.isFinal ?
                      styles.calendarBodyCellSelected :
                      styles.calendarBodyCellHighlight :
                      yearObject?.isSelectable ?
                      styles.calendarBodyCellDefault :
                      styles.calendarBodyCellDisabled,
                      yearObject?.isFirst ?
                      styles.calendarBodyBorderFirst : '',
                      yearObject?.isFinal ?
                      styles.calendarBodyBorderFinal : '',
                    ].join(' ')}>
                    <div
                      key={`year-content-${yearArrayIndex}-${yearObjectIndex}`}
                      className={
                        yearObject?.isSelected ?
                        yearObject.isFirst && yearObject.isFinal ?
                        styles.calendarBodyContentDefault :
                        styles.calendarBodyContentSelected :
                        styles.calendarBodyContentDefault
                      }
                      onClick={
                        (yearObject?.isSelectable) ? (() => {
                          store.viewYear.setValue(yearObject.year);
                          store.modeYear.setValue(false);
                          store.modeMonth.setValue(true);
                        }) : undefined
                      }>
                      {new Date(yearObject.year, 0).toLocaleString(dateLocale, cellDateFormat)}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
});

type CalendarInputMonthPickerProps = {
  store: CalendarInputStore;
};
const CalendarInputMonthPicker = observer(({ store }: CalendarInputMonthPickerProps) => {
  const styles = useStyles(themedStyles);

  const dateLocale = localeToLanguageCode(GlobalLocaleStore.get());

  const headerDateFormat: Intl.DateTimeFormatOptions = {
    year: 'numeric',
  };

  const cellDateFormat: Intl.DateTimeFormatOptions = {
    month: 'short',
  };

  const viewYear = store.viewYear.value;

  let tempDateFirst, tempDateFinal;
  const tempDate = new Date(store.tempDateObject);
  tempDate.setDate(1);
  switch (store.type) {
    case CalendarInputType.FIRST:
      tempDateFirst = new Date(store.tempDateObject);
      tempDateFinal = new Date((store as CalendarFirstInputStore).final.committedDateObject);
      break;
    case CalendarInputType.FINAL:
      tempDateFirst = new Date((store as CalendarFinalInputStore).first.committedDateObject);
      tempDateFinal = new Date(store.tempDateObject);
      break;
    default:
      tempDateFirst = tempDateFinal = new Date(store.tempDateObject);
      break;
  }
  tempDateFirst.setDate(1);
  tempDateFinal.setDate(1);

  const viewDateFirst = new Date(viewYear + 0, 0, 1);
  const viewDateFinal = new Date(viewYear + 1, 0, 0);

  const viewDateFirstMonth = viewDateFirst.getMonth();
  const viewDateFinalMonth = viewDateFinal.getMonth();

  const calendarHasPrev = (store.type === CalendarInputType.SINGLE ||
                           store.type === CalendarInputType.FIRST ||
                           (store.type === CalendarInputType.FINAL &&
                            tempDateFirst <= new Date(viewYear + 0, 0, 0)));
  const calendarHasNext = (store.type === CalendarInputType.SINGLE ||
                           (store.type === CalendarInputType.FIRST &&
                            new Date(viewYear + 1, 0, 1) <= tempDateFinal) ||
                           store.type === CalendarInputType.FINAL);

  const calendarArray = Array(4).fill(null).map(() => Array(3).fill(null));
  for (let viewMonth = viewDateFirstMonth; viewMonth <= viewDateFinalMonth; viewMonth++) {
    const calendarDate = new Date(viewYear, viewMonth);
    const row = Math.floor(viewMonth / 3);
    const col = viewMonth % 3;
    calendarArray[row][col] = {
      year: viewYear, month: viewMonth,
      isFirst: calendarDate.getTime() === tempDateFirst.getTime(),
      isFinal: calendarDate.getTime() === tempDateFinal.getTime(),
      isSelected: calendarDate.getTime() === tempDate.getTime(),
      isHighlight: (tempDateFirst <= calendarDate &&
                    calendarDate <= tempDateFinal),
      isSelectable: (store.type === CalendarInputType.SINGLE ||
                     (store.type === CalendarInputType.FIRST &&
                      calendarDate <= tempDateFinal) ||
                     (store.type === CalendarInputType.FINAL &&
                      tempDateFirst <= calendarDate)),
    };
  }

  return (
    <div className={styles.calendar}>
      <div className={styles.calendarHeader}>
        <div
          className={
            calendarHasPrev ?
            styles.calendarHeaderNavPrev :
            styles.calendarHeaderNavPrevDisabled
          }
          onClick={
            calendarHasPrev ? () => {
              const newViewDate = new Date(viewYear - 1, 0);
              store.viewYear.setValue(newViewDate.getFullYear());
            } : undefined
          }/>
        <div
          className={styles.calendarHeaderText}
          onClick={() => {
            store.modeMonth.setValue(false);
            store.modeYear.setValue(true);
          }}>
          {viewDateFirst.toLocaleString(dateLocale, headerDateFormat)}
        </div>
        <div
          className={
            calendarHasNext ?
            styles.calendarHeaderNavNext :
            styles.calendarHeaderNavNextDisabled
          }
          onClick={
            calendarHasNext ? () => {
              const newViewDate = new Date(viewYear + 1, 0);
              store.viewYear.setValue(newViewDate.getFullYear());
            } : undefined
          }/>
      </div>
      <div className={styles.calendarBody}>
        {calendarArray.map((quarterArray, quarterArrayIndex) => {
          return (
            <div
              key={`month-row-${quarterArrayIndex}`}
              className={styles.calendarBodyRow}>
              {quarterArray.map((monthObject, monthObjectIndex) => {
                return (
                  <div
                    key={`month-cell-${quarterArrayIndex}-${monthObjectIndex}`}
                    className={[
                      monthObject?.isHighlight ?
                      monthObject.isFirst && monthObject.isFinal ?
                      styles.calendarBodyCellSelected :
                      styles.calendarBodyCellHighlight :
                      monthObject?.isSelectable ?
                      styles.calendarBodyCellDefault :
                      styles.calendarBodyCellDisabled,
                      monthObject?.isFirst ?
                      styles.calendarBodyBorderFirst : '',
                      monthObject?.isFinal ?
                      styles.calendarBodyBorderFinal : '',
                    ].join(' ')}>
                    <div
                      key={`month-content-${quarterArrayIndex}-${monthObjectIndex}`}
                      className={
                        monthObject?.isSelected ?
                        monthObject.isFirst && monthObject.isFinal ?
                        styles.calendarBodyContentDefault :
                        styles.calendarBodyContentSelected :
                        styles.calendarBodyContentDefault
                      }
                      onClick={
                        (monthObject?.isSelectable) ? (() => {
                          store.viewYear.setValue(monthObject.year);
                          store.viewMonth.setValue(monthObject.month);
                          store.modeMonth.setValue(false);
                          store.modeDate.setValue(true);
                        }) : undefined
                      }>
                      {new Date(monthObject.year, monthObject.month).toLocaleString(dateLocale, cellDateFormat)}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
});

type CalendarInputDatePickerProps = {
  store: CalendarInputStore;
};
const CalendarInputDatePicker = observer(({ store }: CalendarInputDatePickerProps) => {
  const styles = useStyles(themedStyles);

  const dateLocale = localeToLanguageCode(GlobalLocaleStore.get());

  const headerDateFormat: Intl.DateTimeFormatOptions = {
    month: 'long',
    year: 'numeric',
  };

  const viewYear = store.viewYear.value;
  const viewMonth = store.viewMonth.value;

  let tempDateFirst, tempDateFinal;
  const tempDate = store.tempDateObject;
  switch (store.type) {
    case CalendarInputType.FIRST:
      tempDateFirst = store.tempDateObject;
      tempDateFinal = (store as CalendarFirstInputStore).final.committedDateObject;
      break;
    case CalendarInputType.FINAL:
      tempDateFirst = (store as CalendarFinalInputStore).first.committedDateObject;
      tempDateFinal = store.tempDateObject;
      break;
    default:
      tempDateFirst = tempDateFinal = store.tempDateObject;
      break;
  }

  const viewDateFirst = new Date(viewYear, viewMonth + 0, 1);
  const viewDateFinal = new Date(viewYear, viewMonth + 1, 0);

  const viewDateFirstDate = viewDateFirst.getDate();
  const viewDateFinalDate = viewDateFinal.getDate();

  const viewDateIndexFirst = viewDateFirstDate - viewDateFirst.getDay();

  const calendarHasPrev = (store.type === CalendarInputType.SINGLE ||
                           store.type === CalendarInputType.FIRST ||
                           (store.type === CalendarInputType.FINAL &&
                            tempDateFirst <= new Date(viewYear, viewMonth + 0, 0)));
  const calendarHasNext = (store.type === CalendarInputType.SINGLE ||
                           (store.type === CalendarInputType.FIRST &&
                            new Date(viewYear, viewMonth + 1, 1) <= tempDateFinal) ||
                           store.type === CalendarInputType.FINAL);

  const calendarArray = Array(6).fill(null).map(() => Array(7).fill(null));
  for (let viewDate = viewDateFirstDate; viewDate <= viewDateFinalDate; viewDate++) {
    const calendarDate = new Date(viewYear, viewMonth, viewDate);
    const row = Math.floor((viewDate - viewDateIndexFirst) / 7);
    const col = (viewDate - viewDateIndexFirst) % 7;
    calendarArray[row][col] = {
      calendarDate, year: viewYear, month: viewMonth, date: viewDate,
      isFirst: calendarDate.getTime() === tempDateFirst.getTime(),
      isFinal: calendarDate.getTime() === tempDateFinal.getTime(),
      isSelected: calendarDate.getTime() === tempDate.getTime(),
      isHighlight: (tempDateFirst <= calendarDate &&
                    calendarDate <= tempDateFinal),
      isSelectable: (store.type === CalendarInputType.SINGLE ||
                     (store.type === CalendarInputType.FIRST &&
                      calendarDate <= tempDateFinal) ||
                     (store.type === CalendarInputType.FINAL &&
                      tempDateFirst <= calendarDate)),
    };
  }

  return (
    <div className={styles.calendar}>
      <div className={styles.calendarHeader}>
        <div
          className={
            calendarHasPrev ?
            styles.calendarHeaderNavPrev :
            styles.calendarHeaderNavPrevDisabled
          }
          onClick={
            calendarHasPrev ? () => {
              const newViewDate = new Date(viewYear, viewMonth - 1);
              store.viewYear.setValue(newViewDate.getFullYear());
              store.viewMonth.setValue(newViewDate.getMonth());
            } : undefined
          }/>
        <div
          className={styles.calendarHeaderText}
          onClick={() => {
            store.modeDate.setValue(false);
            store.modeMonth.setValue(true);
          }}>
          {viewDateFirst.toLocaleString(dateLocale, headerDateFormat)}
        </div>
        <div
          className={
            calendarHasNext ?
            styles.calendarHeaderNavNext :
            styles.calendarHeaderNavNextDisabled
          }
          onClick={
            calendarHasNext ? () => {
              const newViewDate = new Date(viewYear, viewMonth + 1);
              store.viewYear.setValue(newViewDate.getFullYear());
              store.viewMonth.setValue(newViewDate.getMonth());
            } : undefined
          }/>
      </div>
      <div className={styles.calendarBody}>
        <div className={styles.calendarBodyRow}>
          {ORDERED_WEEKDAYS.map((weekDay, weekDayIndex) => {
            return (
              <div
                key={`day-cell-${weekDayIndex}`}
                className={styles.calendarBodyCellType}>
                {getMicroWeekday(weekDay)}
              </div>
            );
          })}
        </div>
        {calendarArray.map((weekArray, weekArrayIndex) => {
          return (
            <div
              key={`date-row-${weekArrayIndex}`}
              className={styles.calendarBodyRow}>
              {weekArray.map((dateObject, dateObjectIndex) => {
                return (
                  <div
                    key={`date-cell-${weekArrayIndex}-${dateObjectIndex}`}
                    className={[
                      dateObject?.isHighlight ?
                      dateObject.isFirst && dateObject.isFinal ?
                      styles.calendarBodyCellSelected :
                      styles.calendarBodyCellHighlight :
                      dateObject?.isSelectable ?
                      styles.calendarBodyCellDefault :
                      styles.calendarBodyCellDisabled,
                      dateObject?.isFirst ?
                      styles.calendarBodyBorderFirst : '',
                      dateObject?.isFinal ?
                      styles.calendarBodyBorderFinal : '',
                    ].join(' ')}>
                    <div
                      key={`date-content-${weekArrayIndex}-${dateObjectIndex}`}
                      className={
                        dateObject?.isSelected ?
                        dateObject.isFirst && dateObject.isFinal ?
                        styles.calendarBodyContentDefault :
                        styles.calendarBodyContentSelected :
                        styles.calendarBodyContentDefault
                      }
                      onClick={
                        (dateObject?.isSelectable) ? (() => {
                          store.tempYear.setValue(dateObject.year);
                          store.tempMonth.setValue(dateObject.month);
                          store.tempDate.setValue(dateObject.date);
                        }) : undefined
                      }>
                      {dateObject ? dateObject.date : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
});

type CalendarInputModalProps = {
  store: CalendarInputStore;
};
const CalendarInputModal = observer(({ store }: CalendarInputModalProps) => {
  const styles = useStyles(themedStyles);

  const onCommit = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.commitDate();
    store.modal.hideModal();
  }), [store]);

  return (
    <div className={styles.modalContainer}>
      <StandardPage>
        <div className={styles.header}>
          <BackButton onClick={store.modal.hideModal}/>
          <button type='button' className={styles.commit} onClick={onCommit}>
            {CalendarInputMessages.ok()}
          </button>
        </div>
        {store.modeYear.value && <CalendarInputYearPicker store={store} />}
        {store.modeMonth.value && <CalendarInputMonthPicker store={store} />}
        {store.modeDate.value && <CalendarInputDatePicker store={store} />}
      </StandardPage>
    </div>
  );
});

type CalendarInputBoxProps = {
  store: CalendarInputStore;
};
const CalendarInputBox = observer(({ store }: CalendarInputBoxProps) => {
  const styles = useStyles(themedStyles);

  const onShowModal = React.useCallback(action(() => {
    store.reloadDate();
    store.modal.showModal();
  }), [store]);

  const dateLocale = localeToLanguageCode(GlobalLocaleStore.get());
  const dateFormat: Intl.DateTimeFormatOptions = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
  };

  return (
    <button type='button' className={styles.box} onClick={onShowModal}>
      {store.committedDateObject.toLocaleString(dateLocale, dateFormat)}
    </button>
  );
});

export type CalendarInputProps = {
  store: CalendarInputStore;
};
export const CalendarInput = observer(({ store }: CalendarInputProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <CalendarInputModal store={store}/>
      </Modal>
      <CalendarInputBox store={store}/>
    </>
  );
});
